import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowDownRight,
  ArrowUpRight,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Copy,
  Download,
  RefreshCcw,
  Search,
  Wallet,
  ArrowUpFromLine,
} from "lucide-react";

import { Statuses } from "src/helpers/constants";
import {
  formatAmount,
  getWalletBalance,
  getWalletBlockedBalance,
  getWalletCurrencyCode,
  getWalletLabel,
  getWalletNumber,
  normalizeWalletList,
  WalletLike,
} from "src/helpers/utils";
import { getAllTransferHistory, getUser, getUserWallet } from "src/services";
import WithdrawWalletModal, { isCurrencyWithdrawable } from "./common/WithdrawWalletModal";
import Banner from "./common/Banner";
import TransferDetails from "./common/TransferDetails";
import { Input } from "./common/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./common/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./common/ui/table";
import { LoadingContainer, Title, TitleDescription, TitleWrapper, Wrapper } from "./Styles";

type WalletTransaction = {
  id?: string | number;
  reference?: string;
  amount?: string | number;
  charges?: string | number;
  currency_code?: string;
  transaction_type?: string;
  status?: string;
  channel?: string;
  description?: string;
  created_at?: string;
  sender_wallet_number?: string;
  receiver_wallet_number?: string;
  debit_or_credit?: string;
  wallet_number?: string;
  sender_id?: string | number;
  receiver_id?: string | number;
  source_metadata?: Record<string, unknown>;
  destination_metadata?: Record<string, unknown>;
};

type WalletFilters = {
  query: string;
  startDate: string;
  endDate: string;
  status: string;
};

const PAGE_SIZE = 15;

const hasStoredAuth = () => Boolean(localStorage.getItem("access") || localStorage.getItem("refresh"));

const walletThemes = [
  {
    card: "bg-gradient-to-br from-rose-600 via-rose-700 to-red-950 text-white",
    helper: "text-white/75",
    number: "text-white/90",
    border: "border-white/10",
    chip: "bg-white/10 text-white",
    chipTone: "text-white/70",
  },
  {
    card: "bg-gradient-to-br from-slate-100 via-white to-slate-200 text-slate-900",
    helper: "text-slate-500",
    number: "text-slate-700",
    border: "border-slate-200",
    chip: "bg-white text-slate-800",
    chipTone: "text-slate-500",
  },
  {
    card: "bg-gradient-to-br from-zinc-900 via-slate-800 to-slate-600 text-white",
    helper: "text-white/60",
    number: "text-white/80",
    border: "border-white/10",
    chip: "bg-white/10 text-white",
    chipTone: "text-white/60",
  },
];

const statusOptions = [{ label: "All", value: "all" }, ...Statuses.filter(({ value }) => value !== "all")];

const maskWalletNumber = (value?: string) => {
  if (!value) {
    return "Wallet number unavailable";
  }

  const cleaned = value.replace(/\s+/g, "");
  if (cleaned.length <= 4) {
    return cleaned;
  }

  return `•••• ${cleaned.slice(-4)}`;
};

const toDateKey = (value?: string) => {
  if (!value) {
    return "";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return parsed.toLocaleDateString("en-CA");
};

const formatDate = (value?: string) => {
  if (!value) {
    return "Recent";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const normalizeSearchText = (transaction: WalletTransaction) =>
  [
    transaction.reference,
    transaction.description,
    transaction.channel,
    transaction.sender_wallet_number,
    transaction.receiver_wallet_number,
    transaction.wallet_number,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const matchesSelectedWallet = (transaction: WalletTransaction, selectedWalletNumber: string) => {
  const sender = getWalletNumber({ number: transaction.sender_wallet_number });
  const receiver = getWalletNumber({ number: transaction.receiver_wallet_number });
  const walletNumber = getWalletNumber({ number: transaction.wallet_number });

  return (
    sender === selectedWalletNumber ||
    receiver === selectedWalletNumber ||
    walletNumber === selectedWalletNumber
  );
};

const getDirection = (transaction: WalletTransaction, selectedWalletNumber: string) => {
  const sender = getWalletNumber({ number: transaction.sender_wallet_number });
  const directDirection = (transaction.debit_or_credit || "").toLowerCase();

  if (directDirection.includes("debit")) {
    return "Debit";
  }

  if (directDirection.includes("credit")) {
    return "Credit";
  }

  return sender && sender === selectedWalletNumber ? "Debit" : "Credit";
};

const getCounterparty = (transaction: WalletTransaction, selectedWalletNumber: string) => {
  const sender = getWalletNumber({ number: transaction.sender_wallet_number });
  const receiver = getWalletNumber({ number: transaction.receiver_wallet_number });

  if (sender && sender === selectedWalletNumber) {
    return receiver || transaction.description || transaction.reference || "Debit";
  }

  if (receiver && receiver === selectedWalletNumber) {
    return sender || transaction.description || transaction.reference || "Credit";
  }

  return transaction.description || transaction.reference || transaction.channel || "Transfer";
};

const getStatusTone = (status?: string) => {
  const normalized = (status || "").toLowerCase();

  if (normalized.includes("complete") || normalized.includes("success")) {
    return "bg-emerald-50 text-emerald-700 ring-emerald-100";
  }

  if (normalized.includes("pending")) {
    return "bg-amber-50 text-amber-700 ring-amber-100";
  }

  if (normalized.includes("fail") || normalized.includes("cancel")) {
    return "bg-rose-50 text-rose-700 ring-rose-100";
  }

  return "bg-slate-100 text-slate-600 ring-slate-200";
};

const escapeCsvValue = (value: unknown) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const Wallets = () => {
  const [selectedWalletNumber, setSelectedWalletNumber] = useState("");
  const [historyPage, setHistoryPage] = useState(1);
  const [copiedWalletNumber, setCopiedWalletNumber] = useState<string | null>(null);
  const [filters, setFilters] = useState<WalletFilters>({
    query: "",
    startDate: "",
    endDate: "",
    status: "all",
  });
  const [openTransactionDetails, setOpenTransactionDetails] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<WalletTransaction | null>(null);
  const [withdrawWallet, setWithdrawWallet] = useState<{ currency: string; balance: string } | null>(null);

  const {
    data: userData,
    isLoading: userIsLoading,
    isError: userIsError,
    refetch: refetchUser,
  } = useQuery({
    queryKey: ["userDetails"],
    queryFn: () => getUser(),
    enabled: hasStoredAuth(),
  });

  const {
    isLoading: walletQueryIsLoading,
    isError: walletQueryIsError,
    data: walletData,
    refetch: refetchWallets,
  } = useQuery({
    queryKey: ["userWallets", userData?.id],
    queryFn: () => getUserWallet(String(userData?.id)),
    enabled: Boolean(userData?.id),
    retry: false,
  });

  const walletIsLoading = userIsLoading || walletQueryIsLoading;
  const walletIsError = userIsError || walletQueryIsError;

  const wallets = useMemo(
    () => normalizeWalletList<WalletLike>(walletData),
    [walletData],
  );

  useEffect(() => {
    if (!wallets.length) {
      return;
    }

    const firstWalletNumber = getWalletNumber(wallets[0]);
    const selectedWalletExists = wallets.some(
      (wallet) => getWalletNumber(wallet) === selectedWalletNumber,
    );

    if (!selectedWalletNumber && firstWalletNumber) {
      setSelectedWalletNumber(firstWalletNumber);
      return;
    }

    if (!selectedWalletExists && firstWalletNumber) {
      setSelectedWalletNumber(firstWalletNumber);
    }
  }, [wallets, selectedWalletNumber]);

  const {
    data: allHistory,
    isLoading: historyIsLoading,
    isError: historyIsError,
    refetch: refetchHistory,
  } = useQuery({
    queryKey: ["walletTransactions", userData?.id],
    queryFn: () => getAllTransferHistory(String(userData?.id)),
    enabled: Boolean(userData?.id),
    staleTime: 5 * 60 * 1000,
  });

  const allTransactions = useMemo(
    () => ((allHistory?.results ?? []) as WalletTransaction[]),
    [allHistory],
  );

  const selectedWallet = wallets.find((wallet) => getWalletNumber(wallet) === selectedWalletNumber);
  const selectedWalletIndex = Math.max(
    0,
    wallets.findIndex((wallet) => getWalletNumber(wallet) === selectedWalletNumber),
  );
  const selectedWalletBalance = selectedWallet ? getWalletBalance(selectedWallet) : "0.00";

  const selectedWalletCurrency = selectedWallet
    ? getWalletLabel(selectedWallet, selectedWalletIndex)
    : "Wallet";
  const selectedWalletTheme = walletThemes[selectedWalletIndex % walletThemes.length];

  const selectedWalletTransactions = useMemo(
    () =>
      allTransactions.filter((transaction) =>
        matchesSelectedWallet(transaction, selectedWalletNumber),
      ),
    [allTransactions, selectedWalletNumber],
  );

  const filteredTransactions = useMemo(() => {
    const search = filters.query.trim().toLowerCase();
    const status = filters.status.toLowerCase();
    const startDate = filters.startDate ? new Date(filters.startDate) : null;
    const endDate = filters.endDate ? new Date(filters.endDate) : null;

    return selectedWalletTransactions.filter((transaction) => {
      const matchesSearch = !search || normalizeSearchText(transaction).includes(search);
      const transactionDateKey = toDateKey(transaction.created_at);
      const transactionDate = transactionDateKey ? new Date(transactionDateKey) : null;
      const matchesStartDate = !startDate || (transactionDate ? transactionDate >= startDate : false);
      const matchesEndDate = !endDate || (transactionDate ? transactionDate <= endDate : false);
      const transactionStatus = (transaction.status || "").toLowerCase();
      const matchesStatus =
        status === "all" || (transactionStatus ? transactionStatus === status : false);

      return matchesSearch && matchesStartDate && matchesEndDate && matchesStatus;
    });
  }, [filters.endDate, filters.query, filters.startDate, filters.status, selectedWalletTransactions]);

  const totalPages = Math.max(1, Math.ceil(filteredTransactions.length / PAGE_SIZE));

  useEffect(() => {
    if (historyPage > totalPages) {
      setHistoryPage(totalPages);
    }
  }, [historyPage, totalPages]);

  useEffect(() => {
    setHistoryPage(1);
    setOpenTransactionDetails(false);
    setSelectedTransaction(null);
  }, [filters.endDate, filters.query, filters.startDate, filters.status, selectedWalletNumber]);

  const pagedTransactions = useMemo(
    () => filteredTransactions.slice((historyPage - 1) * PAGE_SIZE, historyPage * PAGE_SIZE),
    [filteredTransactions, historyPage],
  );

  const isPendingActivation = !(
    userData?.user_images?.profile_picture && userData?.user_images?.user_id_image
  );

  const handleSelectWallet = (walletNumber: string) => {
    if (!walletNumber) {
      return;
    }

    setSelectedWalletNumber(walletNumber);
    setHistoryPage(1);
    setOpenTransactionDetails(false);
    setSelectedTransaction(null);
  };

  const updateFilter = (partial: Partial<WalletFilters>) => {
    setFilters((current) => ({ ...current, ...partial }));
    setHistoryPage(1);
  };

  const handleCopyWalletNumber = async (walletNumber: string) => {
    if (!walletNumber) {
      return;
    }

    try {
      await navigator.clipboard.writeText(walletNumber);
      setCopiedWalletNumber(walletNumber);
      window.setTimeout(
        () => setCopiedWalletNumber((current) => (current === walletNumber ? null : current)),
        1500,
      );
    } catch {
      setCopiedWalletNumber(walletNumber);
    }
  };

  const handleExportCsv = () => {
    if (!filteredTransactions.length) {
      return;
    }

    const headers = [
      "Date",
      "Direction",
      "Counterparty",
      "Reference",
      "Amount",
      "Charges",
      "Currency",
      "Status",
      "Channel",
      "Description",
      "Sender Wallet",
      "Receiver Wallet",
    ];

    const rows = filteredTransactions.map((transaction) => {
      const direction = getDirection(transaction, selectedWalletNumber);

      return [
        formatDate(transaction.created_at),
        direction,
        getCounterparty(transaction, selectedWalletNumber),
        transaction.reference || "",
        `${transaction.currency_code ? `${transaction.currency_code} ` : ""}${formatAmount(
          transaction.amount ?? 0,
        )}`,
        formatAmount(transaction.charges ?? 0),
        transaction.currency_code || "",
        transaction.status || "",
        transaction.channel || "",
        transaction.description || "",
        transaction.sender_wallet_number || "",
        transaction.receiver_wallet_number || "",
      ];
    });

    const csv = [headers, ...rows]
      .map((row) => row.map((value) => escapeCsvValue(value)).join(","))
      .join("\n");

    const blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `wallet-history-${selectedWalletNumber}-page-${historyPage}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleOpenTransaction = (transaction: WalletTransaction) => {
    setSelectedTransaction(transaction);
    setOpenTransactionDetails(true);
  };

  const handleRowKeyDown = (
    event: { key: string; preventDefault: () => void },
    transaction: WalletTransaction,
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleOpenTransaction(transaction);
    }
  };

  const pageCountLabel = `Page ${historyPage} of ${totalPages}`;
  const pageTransactionsLabel = `${pagedTransactions.length} transaction${
    pagedTransactions.length === 1 ? "" : "s"
  } on this page`;

  return (
    <Wrapper>
      {isPendingActivation && <Banner />}

      <div className="relative overflow-hidden rounded-[36px] border border-slate-200/80 bg-gradient-to-br from-slate-50 via-white to-rose-50 p-5 shadow-[0_24px_70px_rgba(15,23,42,0.08)]">
        <div className="pointer-events-none absolute -right-16 top-8 h-40 w-40 rounded-full bg-rose-200/40 blur-3xl" />
        <div className="pointer-events-none absolute -left-10 bottom-0 h-40 w-40 rounded-full bg-slate-200/50 blur-3xl" />

        <div className="relative space-y-6">
          <TitleWrapper className="!m-0">
            <p className="text-xs font-semibold uppercase tracking-[0.32em] text-rose-500">
              Wallet Center
            </p>
            <Title>Your Wallets</Title>
            <TitleDescription fontSize="0.92rem">
              Select a wallet card to inspect its balance summary and transaction history.
            </TitleDescription>
          </TitleWrapper>

          {walletIsLoading && !wallets.length && (
            <LoadingContainer>
              <p>Loading wallets...</p>
            </LoadingContainer>
          )}

          {walletIsError && !wallets.length && (
            <LoadingContainer>
              <p>There was an error getting wallets.</p>
              <button onClick={() => refetchWallets()} className="text-rose-600">
                Click to retry
              </button>
            </LoadingContainer>
          )}

          {wallets.length > 0 ? (
            <div className="space-y-6">
              <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {wallets.map((wallet, index) => {
                  const walletNumber = getWalletNumber(wallet);
                  const walletBalance = getWalletBalance(wallet);
                  const walletCurrency = getWalletLabel(wallet, index);
                  const theme = walletThemes[index % walletThemes.length];
                  const isSelected = walletNumber === selectedWalletNumber;

                  return (
                    <article
                      key={wallet.id ?? walletNumber ?? index}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleSelectWallet(walletNumber)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          handleSelectWallet(walletNumber);
                        }
                      }}
                      className={`relative min-h-[220px] overflow-hidden rounded-[28px] border p-5 shadow-[0_18px_40px_rgba(15,23,42,0.12)] transition duration-200 hover:-translate-y-0.5 ${
                        theme.card
                      } ${theme.border} ${
                        isSelected ? "ring-2 ring-rose-300 ring-offset-2 ring-offset-white" : "opacity-95"
                      }`}
                    >
                      <div
                        className={`absolute -right-10 -top-10 h-32 w-32 rounded-full ${
                          index % 2 === 1 ? "bg-white/60" : "bg-white/10"
                        } blur-3xl`}
                      />

                      <div className="relative flex h-full flex-col justify-between">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className={`text-[11px] font-semibold uppercase tracking-[0.24em] ${theme.helper}`}>
                              {walletCurrency}
                            </p>
                            <p className={`mt-2 text-xs font-medium ${theme.chipTone}`}>
                              {isSelected ? "Selected wallet" : `Wallet #${index + 1}`}
                            </p>
                          </div>

                          <div
                            className={`flex h-10 w-10 items-center justify-center rounded-full ${
                              index % 3 === 1 ? "bg-white text-slate-900" : "bg-white/15 text-white"
                            }`}
                          >
                            <Wallet className="h-4 w-4" />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div className={`rounded-2xl ${theme.chip} p-4`}>
                            <p className={`text-[10px] uppercase tracking-[0.18em] ${theme.chipTone}`}>
                              Available
                            </p>
                            <p className="mt-2 text-2xl font-black tracking-tight">{walletBalance}</p>
                            {getWalletBlockedBalance(wallet) > 0 && (
                              <p className={`mt-1 text-[11px] ${theme.chipTone}`}>
                                In trade: {formatAmount(getWalletBlockedBalance(wallet))}
                              </p>
                            )}
                          </div>

                          <div className={`rounded-2xl ${theme.chip} p-4`}>
                            <p className={`text-[10px] uppercase tracking-[0.18em] ${theme.chipTone}`}>
                              Wallet
                            </p>
                            <p className="mt-2 font-mono text-sm font-semibold">
                              {maskWalletNumber(walletNumber)}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-end justify-between gap-4">
                          <div>
                            <p className={`text-[10px] uppercase tracking-[0.18em] ${theme.helper}`}>
                              Wallet number
                            </p>
                            <p className={`mt-1 font-mono text-sm font-semibold ${theme.number}`}>
                              {walletNumber || "Pending"}
                            </p>
                          </div>

                          <div className="flex items-center gap-2">
                            {isCurrencyWithdrawable(getWalletCurrencyCode(wallet)) && (
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setWithdrawWallet({ currency: getWalletCurrencyCode(wallet), balance: getWalletBalance(wallet) });
                                }}
                                className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-[11px] font-semibold transition ${
                                  index % 3 === 1
                                    ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                    : "border-white/15 bg-white/10 text-white hover:bg-white/15"
                                }`}
                              >
                                <ArrowUpFromLine className="h-3.5 w-3.5" />
                                Withdraw
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleCopyWalletNumber(walletNumber);
                              }}
                              disabled={!walletNumber}
                              className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-[11px] font-semibold transition ${
                                index % 3 === 1
                                  ? "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                                  : "border-white/15 bg-white/10 text-white hover:bg-white/15"
                              } disabled:cursor-not-allowed disabled:opacity-60`}
                            >
                              {copiedWalletNumber === walletNumber ? (
                                <>
                                  <Check className="h-3.5 w-3.5" />
                                  Copied
                                </>
                              ) : (
                                <>
                                  <Copy className="h-3.5 w-3.5" />
                                  Copy
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </section>

              <section className="rounded-[32px] border border-slate-200 bg-white/80 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.06)] backdrop-blur">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.28em] text-rose-500">History</p>
                    <h2 className="mt-2 text-2xl font-bold text-slate-900">Selected wallet history</h2>
                    <p className="mt-2 max-w-2xl text-sm text-slate-500">
                      Transactions linked to {maskWalletNumber(selectedWalletNumber)}.
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                      <Clock3 className="h-3.5 w-3.5" />
                      {pageCountLabel}
                    </span>
                    <span className="rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-600">
                      {pageTransactionsLabel}
                    </span>
                  </div>
                </div>

                <div className="mt-5 rounded-[28px] border border-slate-200 bg-slate-50/70 p-4">
                  <div className="grid gap-4 xl:grid-cols-[minmax(0,1.4fr)_minmax(160px,0.7fr)_minmax(160px,0.7fr)_minmax(180px,0.8fr)_auto]">
                    <div className="space-y-2">
                      <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Reference
                      </label>
                      <div className="relative">
                        <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <Input
                          value={filters.query}
                          onChange={(event) => updateFilter({ query: event.target.value })}
                          placeholder="Search by reference"
                          className="pl-11"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Date From
                      </label>
                      <Input
                        type="date"
                        value={filters.startDate}
                        onChange={(event) => updateFilter({ startDate: event.target.value })}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Date To
                      </label>
                      <Input
                        type="date"
                        value={filters.endDate}
                        onChange={(event) => updateFilter({ endDate: event.target.value })}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Status
                      </label>
                      <Select
                        value={filters.status}
                        onValueChange={(value) => updateFilter({ status: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          {statusOptions.map(({ label, value }) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={handleExportCsv}
                        disabled={!filteredTransactions.length}
                        className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-2xl border border-rose-600 bg-rose-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                      >
                        <Download className="h-4 w-4" />
                        Export
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-4 rounded-[26px] border border-slate-200 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.04)]">
                  <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[140px]">Direction</TableHead>
                        <TableHead>Counterparty</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>

                    <TableBody>
                      {historyIsLoading ? (
                        <TableRow>
                          <TableCell colSpan={5} className="py-12 text-center text-slate-500">
                            Loading history...
                          </TableCell>
                        </TableRow>
                      ) : historyIsError ? (
                        <TableRow>
                          <TableCell colSpan={5} className="py-12 text-center">
                            <div className="flex flex-col items-center gap-3">
                              <p className="text-slate-600">Could not load history.</p>
                              <button
                                type="button"
                                onClick={() => refetchHistory()}
                                className="inline-flex items-center gap-2 rounded-full bg-rose-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-rose-700"
                              >
                                <RefreshCcw className="h-4 w-4" />
                                Retry
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : filteredTransactions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="py-12 text-center text-slate-500">
                            No transactions found for this wallet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        pagedTransactions.map((transaction) => {
                          const direction = getDirection(transaction, selectedWalletNumber);
                          const counterparty = getCounterparty(transaction, selectedWalletNumber);
                          const amount = formatAmount(transaction.amount ?? 0);
                          const statusTone = getStatusTone(transaction.status);
                          const isDebit = direction === "Debit";

                          return (
                            <TableRow
                              key={transaction.id ?? `${transaction.reference ?? ""}-${transaction.created_at ?? ""}`}
                              onClick={() => handleOpenTransaction(transaction)}
                              onKeyDown={(event) => handleRowKeyDown(event, transaction)}
                              tabIndex={0}
                              role="button"
                              aria-label={`Open transaction ${transaction.reference || transaction.id || ""}`}
                            >
                              <TableCell>
                                <div className="flex items-center gap-2 font-medium">
                                  <span
                                    className={`inline-flex h-8 w-8 items-center justify-center rounded-full ${
                                      isDebit ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-600"
                                    }`}
                                  >
                                    {isDebit ? (
                                      <ArrowDownRight className="h-4 w-4" />
                                    ) : (
                                      <ArrowUpRight className="h-4 w-4" />
                                    )}
                                  </span>
                                  {direction}
                                </div>
                              </TableCell>

                              <TableCell>
                                <div className="flex flex-col">
                                  <span className="font-medium text-slate-900">{counterparty}</span>
                                  <span className="text-xs text-slate-500">
                                    {transaction.reference ||
                                      transaction.description ||
                                      transaction.channel ||
                                      "Wallet transfer"}
                                  </span>
                                </div>
                              </TableCell>

                              <TableCell>
                                <div className="flex flex-col">
                                  <span className={`font-semibold ${isDebit ? "text-rose-600" : "text-emerald-600"}`}>
                                    {isDebit ? "-" : "+"}
                                    {transaction.currency_code ? `${transaction.currency_code} ` : ""}
                                    {amount}
                                  </span>
                                  <span className="text-xs text-slate-500">
                                    Charges: {formatAmount(transaction.charges ?? 0)}
                                  </span>
                                </div>
                              </TableCell>

                              <TableCell>
                                <span
                                  className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ring-1 ${statusTone}`}
                                >
                                  {transaction.status || "Unknown"}
                                </span>
                              </TableCell>

                              <TableCell className="text-sm text-slate-500">
                                {formatDate(transaction.created_at)}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                  </div>
                </div>

                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-slate-500">
                    {selectedWalletNumber
                      ? `Browsing ${maskWalletNumber(selectedWalletNumber)}`
                      : "No wallet selected"}
                  </p>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setHistoryPage((prevPage) => Math.max(prevPage - 1, 1))}
                      disabled={historyPage === 1 || filteredTransactions.length === 0}
                      className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition ${
                        historyPage === 1 || filteredTransactions.length === 0
                          ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </button>

                    <span className="rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700">
                      {pageCountLabel}
                    </span>

                    <button
                      type="button"
                      onClick={() => setHistoryPage((prevPage) => prevPage + 1)}
                      disabled={historyPage === totalPages || filteredTransactions.length === 0}
                      className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition ${
                        historyPage === totalPages || filteredTransactions.length === 0
                          ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                          : "border-rose-600 bg-rose-600 text-white hover:bg-rose-700"
                      }`}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </section>
            </div>
          ) : !walletIsLoading && !walletIsError ? (
            <LoadingContainer>
              <div className="flex flex-col items-center gap-3 rounded-[28px] border border-dashed border-slate-200 bg-white/80 px-8 py-12 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-rose-50 text-rose-600">
                  <Wallet className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">No wallets found</h3>
                <p className="max-w-md text-sm text-slate-500">
                  Once wallets are added to your account, they will show up here as dashboard-style cards
                  with transaction history.
                </p>
              </div>
            </LoadingContainer>
          ) : null}
        </div>
      </div>

      <TransferDetails
        open={openTransactionDetails}
        handleClose={() => setOpenTransactionDetails(false)}
        details={selectedTransaction}
      />

      {withdrawWallet && (
        <WithdrawWalletModal
          open
          handleClose={() => setWithdrawWallet(null)}
          userData={userData}
          walletBalance={withdrawWallet.balance}
          currency={withdrawWallet.currency}
          onSuccess={() => {
            refetchUser();
            refetchWallets();
            refetchHistory();
          }}
        />
      )}
    </Wrapper>
  );
};

export default Wallets;
